<script setup lang="ts">
import {computed} from "vue";
import {ClipboardList} from "@lucide/vue";
import {GirerdAnswer, GirerdAnswers, GirerdLine, GirerdQuestion, GirerdView} from "@/medication-checkup/interfaces/girerd";
import {CARD_SHEET, FLAGGED, pillClasses} from "@/medication-checkup/services/SceneClasses";
import MedicationCheckupYesNo from "@/medication-checkup/components/MedicationCheckupYesNo.vue";
import {GIRERD_READINGS, girerdReadingTitle} from "@/medication-checkup/services/GirerdReadings";

/**
 * The Girerd card: six yes/no questions on how the patient takes their
 * treatments, the retained treatments a « yes » may name, and — once the
 * block is validated — the reading of the score.
 *
 * Answering is local to the scene: the card shows the answers it is handed
 * while live, the ones the server froze once the block is completed.
 */

const props = defineProps<{
    view: GirerdView;
    frozen?: boolean;
    /**
     * The answers given so far, while the questionnaire is live.
     */
    answers?: GirerdAnswers;
    /**
     * The refs of the questions the scene points at as unanswered.
     */
    flagged?: string[];
}>();

const emit = defineEmits<{
    answer: [ref: string, answer: GirerdAnswer];
}>();

const reading = computed(() => {
    return props.view.interpretation ? GIRERD_READINGS[props.view.interpretation.key] : null;
});

const readingLabel = computed(() => girerdReadingTitle(props.view.interpretation));

/**
 * What a question reads as answered: the local answer while live, the one
 * the server froze once the block is completed.
 */
const answerOf = (question: GirerdQuestion): GirerdAnswer | null => {
    if (props.frozen) {
        return question.answer === null ? null : {yes_no: question.answer, line_ids: question.line_ids};
    }

    return props.answers?.[question.ref] ?? null;
};

/**
 * The treatments a question offers to name: every retained one while live,
 * the ones it named once frozen.
 */
const lines = (question: GirerdQuestion): GirerdLine[] => {
    const named = answerOf(question)?.line_ids ?? [];

    return props.frozen
        ? props.view.lines.filter(line => named.includes(line.id))
        : props.view.lines;
};

const showLines = (question: GirerdQuestion): boolean => {
    return answerOf(question)?.yes_no === true && lines(question).length > 0;
};

/**
 * Say yes — keeping the treatments already named, if the answer was already
 * yes.
 */
const pickYes = (question: GirerdQuestion) => {
    const current = answerOf(question);

    emit('answer', question.ref, {
        yes_no: true,
        line_ids: current?.yes_no === true ? current.line_ids : []
    });
};

const pickNo = (question: GirerdQuestion) => {
    emit('answer', question.ref, {yes_no: false, line_ids: []});
};

const toggleLine = (question: GirerdQuestion, line: GirerdLine) => {
    const named = answerOf(question)?.line_ids ?? [];

    emit('answer', question.ref, {
        yes_no: true,
        line_ids: named.includes(line.id) ? named.filter(id => id !== line.id) : [...named, line.id]
    });
};
</script>

<template>
  <div
    :class="CARD_SHEET"
    class="max-w-[640px] rounded-xl overflow-hidden"
  >
    <div class="flex items-center gap-2 px-4 py-3">
      <ClipboardList class="w-[18px] h-[18px] text-primary" />
      <span class="text-sm font-semibold text-primary">Questionnaire d'observance de Girerd</span>
      <span class="text-xs text-muted-foreground">— 6 questions, oui / non</span>
    </div>

    <div
      v-for="(question, index) in view.questions"
      :key="question.ref"
      :class="{[FLAGGED]: flagged?.includes(question.ref)}"
      :data-question-ref="question.ref"
      class="flex flex-col gap-1.5 px-4 py-2 border-t border-gray-light animate-message-in"
      :style="{animationDelay: `${index * 60}ms`}"
    >
      <div class="flex items-center gap-2.5">
        <span
          class="w-[22px] h-[22px] shrink-0 rounded-full bg-primary-lightest text-primary text-xs font-bold flex items-center justify-center"
        >
          {{ index + 1 }}
        </span>

        <span class="flex-1 text-sm leading-snug">{{ question.text }}</span>

        <MedicationCheckupYesNo
          :answer="answerOf(question)?.yes_no ?? null"
          :disabled="frozen"
          @answer="given => given ? pickYes(question) : pickNo(question)"
        />
      </div>

      <div v-if="showLines(question)" class="flex flex-wrap items-center gap-1.5 ml-8">
        <span class="text-xs text-muted-foreground">Traitement concerné (facultatif) :</span>
        <button
          v-for="line in lines(question)"
          :key="line.id"
          class="px-2.5 py-0.5 rounded-full border-[1.5px] border-solid text-xs font-medium disabled:opacity-60"
          :class="pillClasses((answerOf(question)?.line_ids ?? []).includes(line.id))"
          :disabled="frozen"
          @click="toggleLine(question, line)"
        >
          {{ line.label }}
        </button>
      </div>
    </div>

    <div
      v-if="reading && view.interpretation"
      class="flex items-center gap-3 px-4 py-3.5 border-t border-gray-light animate-message-in"
      :class="reading.classes"
    >
      <span class="w-9 h-9 shrink-0 rounded-full bg-white flex items-center justify-center">
        <component :is="reading.icon" class="w-5 h-5" />
      </span>
      <span class="flex flex-col">
        <span class="text-base font-bold">{{ readingLabel }}</span>
        <span class="text-sm opacity-80">Score de Girerd : {{ view.score }} oui / 6</span>
      </span>
    </div>
  </div>
</template>
